import { type ReactNode } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';
import { Card } from './Card';
import { EmptyStateIllustration } from './EmptyStateIllustration';

export interface EmptyStateProps {
  /** Short heading, e.g. "No appointments yet" */
  title: string;
  /** Supporting one-liner explaining the state or next step */
  description?: string;
  /** Optional call to action rendered below the description (e.g. a Button) */
  action?: ReactNode;
  className?: string;
  /**
   * `text` is for tight inline/search contexts, `icon` for standard embedded states, and
   * `animated` for prominent page-level list states. Defaults to `icon`.
   */
  variant?: 'text' | 'icon' | 'animated';
}

/**
 * Shared empty-data-state block with density appropriate to its context. Use the animated
 * illustration sparingly for page-level lists; embedded and search states should stay compact.
 */
export function EmptyState({ title, description, action, className, variant = 'icon' }: EmptyStateProps) {
  return (
    <Card
      className={cn(
        'flex flex-col items-center gap-1 text-center',
        variant === 'animated' ? 'p-10' : variant === 'icon' ? 'p-6' : 'p-4',
        className
      )}
    >
      {variant === 'animated' && (
        <div data-empty-state-illustration className="mb-2 h-[140px] w-[180px]" aria-hidden="true">
          <EmptyStateIllustration />
        </div>
      )}
      {variant === 'icon' && (
        <div
          data-empty-state-icon
          className="mb-2 flex h-10 w-10 items-center justify-center rounded-[10px] bg-[var(--surface-2)] text-[var(--text-3)]"
          aria-hidden="true"
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 7.5h14M7.5 4h9l1.5 3.5v11A1.5 1.5 0 0 1 16.5 20h-9A1.5 1.5 0 0 1 6 18.5v-11L7.5 4Z" />
            <path d="M9.5 11.5h5M10.5 15h3" />
          </svg>
        </div>
      )}
      <h3 className="text-[14px] font-semibold text-[var(--text-2)]">{title}</h3>
      {description && <p className="max-w-sm text-[13px] text-[var(--text-3)]">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </Card>
  );
}
